import React, { useState, useEffect } from "react";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import LoadingBox from "common/components/LoadingBox";
import ErrorBox from "common/components/ErrorBox";
import UserRoute from "./UserRoute.jsx";
import { addNewID } from "core-module/services/ids/actions";
import logout from "common/functions/logout";

import * as Sentry from "@sentry/browser";

import * as api from "core-module/services/api";
import store from "store";
import config from "config.js";
import { addJRR } from "core-module/services/api/actions";

import UserSetUp from "./UserSetUp";

import Button from "common/components/CustomButtons/Button";
import { buildRoutes } from "./UserRouteBuild";

import { isIosDevice, isNativeIosPlatform } from "../deviceInfo";

import {
  ApiUnreachable,
  ApiError,
  InvalidToken,
} from "core-module/services/error";

const downloadAllNames = async (associatedAccounts) => {
  const userNames = {};
  for (let user of associatedAccounts) {
    const r = await api.get("user." + user);
    userNames[user] = r.object.name;
  }
  return userNames;
};

const getGlobalNotification = async () => {
  const response = await api.get("me.1/globalnotification");

  const isIos = await isIosDevice();
  if (response == null && !isIos) {
    return null;
  }

  if ("list" in response) {
    return response.list[0];
  } else {
    return null;
  }
};

const getAssociatedAccounts = async () => {
  const response = await api.get("me.1/associatedaccounts");

  const isIos = await isIosDevice();
  if (response == null && !isIos) {
    return [[], "", []];
  }

  if (
    !("list" in response) ||
    response.list.length === 0 ||
    !("associatedAccounts" in response.list[0])
  ) {
    throw new Error("No associated account");
  }

  let associatedAccounts = [];
  for (let user of response.list[0].associatedAccounts) {
    //Be sure to cast number type on this to work on select menu
    associatedAccounts.push(Number(user));
  }
  associatedAccounts.sort();

  return [
    associatedAccounts,
    response.list[0].email,
    response.list[0].associatedAccountsProperties,
  ];
};

const makePhRequest = async () => {
  const ss = store.getState();

  const url = `${config.API.ADDRESS}${"/?ph=b&target=b"}`;
  const myInit = {
    headers: {
      "x-omicron": ss.services.api.JRR,
    },
  };

  try {
    var pingRes = await fetch(url, myInit);
  } catch (e) {
    throw new ApiUnreachable(e.message);
  }

  if (pingRes.status === 200) {
    const pingResObject = await pingRes.json();
    if ("JRR" in pingResObject) {
      store.dispatch(addJRR(pingResObject.JRR));
    }
  }
};

const UserSplash = (props) => {
  const [loading, setLoading] = useState(true);
  const [shouldFireLoadApp, setShouldFireLoadApp] = useState(true);
  const [shouldFireRoutesBuild, setShouldFireRoutesBuild] = useState(false);
  const [warningActive, setWarningActive] = useState(false);
  const [globalNotification, setGlobalNotification] = useState(null);

  const [userNames, setUserNames] = useState({});
  const [associatedAccounts, setAssociatedAccounts] = useState([]);
  const [defaultRedirect, setDefaultRedirect] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [oldActiveUserId, setOldActiveUserId] = useState(props.activeUserId);
  const [errorType, setErrorType] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const intervalID = window.setInterval(() => {
      makePhRequest().catch((e) => {
        if (e instanceof ApiUnreachable) {
          setErrorMessage(
            "No Internet access or our servers are unavailable. Please try again later."
          );
        } else {
          throw e;
        }
      });
    }, 15 * 60 * 1000); //Refresh Access Token every 15 minutes

    return () => window.clearInterval(intervalID);
  }, []);

  const loadApp = async () => {
    if ((await UserSetUp()) === false) {
      return false;
    }

    const [
      localAssociatedAccounts,
      email,
      associatedAccountsProperties,
    ] = await getAssociatedAccounts();
    store.dispatch(addNewID("email", email));

    const linkedContactId = Number(
      window.localStorage.getItem("newuserLinkedContactId")
    );
    const shouldSelectLinkedContact =
      !isNaN(linkedContactId) &&
      localAssociatedAccounts.indexOf(linkedContactId) !== -1;

    setAssociatedAccounts(localAssociatedAccounts);
    if (shouldSelectLinkedContact) {
      store.dispatch(addNewID("user", linkedContactId));
      window.localStorage.removeItem("newuserLinkedContactId");
    } else if (
      props.activeUserId === "" ||
      localAssociatedAccounts.indexOf(props.activeUserId) === -1
    ) {
      if (localAssociatedAccounts.length > 0) {
        store.dispatch(addNewID("user", localAssociatedAccounts[0]));
      }
    }

    if (Boolean(associatedAccountsProperties[props.activeUserId])) {
      store.dispatch(
        addNewID(
          "isVip",
          associatedAccountsProperties[props.activeUserId]?.isVip
        )
      );
    }

    if (localAssociatedAccounts.length > 0) {
      setUserNames(await downloadAllNames(localAssociatedAccounts));
    }

    const message = await getGlobalNotification();
    if (message != null) {
      setGlobalNotification(message);
    }

    return true;
  };

  const splashCall = async () => {
    try {
      if (shouldFireLoadApp) {
        setShouldFireLoadApp(false);
        if ((await loadApp()) === false) {
          return;
        }
        setShouldFireRoutesBuild(true);
      }

      if (
        shouldFireRoutesBuild === true ||
        oldActiveUserId !== props.activeUserId
      ) {
        setLoading(true);
        setShouldFireRoutesBuild(false);
        setOldActiveUserId(props.activeUserId);
        const [redirectPath, localRoutes, warningScreen] = await buildRoutes();
        setWarningActive(warningScreen);
        setDefaultRedirect(redirectPath);
        setRoutes(localRoutes);
        setLoading(false);

        const offer = window.localStorage.getItem("of");
        if (
          offer &&
          props.location.pathname !== config.MYACCOUNT.APP_PATH + "/user/offer"
        ) {
          props.history.push(config.MYACCOUNT.APP_PATH + "/user/offer");
          return;
        }

        if (oldActiveUserId !== props.activeUserId) {
          if (
            props.location.pathname ===
            config.MYACCOUNT.APP_PATH + "/user/change"
          ) {
            if (props.location.state.action === "upgrade") {
              props.history.push(config.MYACCOUNT.APP_PATH + "/user/package", {
                action: "upgrade",
              });
            }
            if (props.location.state.action === "unlimited") {
              props.history.push(config.MYACCOUNT.APP_PATH + "/user/package", {
                action: "unlimited",
              });
            }
            if (props.location.state.action === "advancedsettings") {
              props.history.push(
                config.MYACCOUNT.APP_PATH + "/user/advancedsettings"
              );
            }
          } else {
            props.history.push(config.MYACCOUNT.APP_PATH + "/");
          }
        }
      }
    } catch (e) {
      if (e instanceof ApiUnreachable) {
        setErrorMessage(
          "Either you don't have Internet access or our servers are unavailable. Please try again later."
        );
      } else if (e instanceof ApiError) {
        Sentry.withScope((scope) => {
          const ss = store.getState();
          if ("user" in ss.services.ids) {
            scope.setUser({ id: ss.services.ids.user });
          }
          Sentry.captureException(e);
        });
        setErrorMessage(e.message);
        setErrorType(1);
      } else if (e instanceof InvalidToken) {
        if (isNativeIosPlatform()) {
          setErrorMessage("Your session has expired. Please sign in again.");
          setErrorType(1);
          setLoading(false);
          return;
        }
        setShouldFireRoutesBuild(false);
        setShouldFireLoadApp(false);
        setLoading(true);
      } else {
        throw e;
      }
    }
  };

  splashCall();

  if (errorMessage.length > 0) {
    if (errorType === 1) {
      return (
        <ErrorBox>
          <p>{errorMessage}</p>
          <Button
            key="logout"
            color="primary"
            onClick={() => {
              logout();
              setLoading(true);
            }}
          >
            Logout and try different account
          </Button>
        </ErrorBox>
      );
    } else {
      return (
        <ErrorBox reload>
          <p>{errorMessage}</p>
        </ErrorBox>
      );
    }
  }

  if (loading) {
    return <LoadingBox />;
  } else {
    return (
      <UserRoute
        globalNotification={globalNotification}
        defaultRedirect={defaultRedirect}
        routes={routes}
        userNames={userNames}
        associatedAccounts={associatedAccounts}
        warningActive={warningActive}
      />
    );
  }
};

const mapStateToProps = (state) => ({
  activeUserId: state.services.ids.user,
});

export default withRouter(connect(mapStateToProps)(UserSplash));
